import { useMemo } from 'react'
import { useDockState, type DockBounds } from '@renderer/overlay/hooks/use-dock-state'
import { useOverlayDisplayDockInsets } from '@renderer/overlay/hooks/use-overlay-display-dock-insets'
import { useViewportSnapshot, type ViewportSnapshot } from '@renderer/overlay/hooks/use-viewport-snapshot'
import { useOverlayMenuBarHeight } from '@renderer/overlay/hooks/use-overlay-notch-bounds'
import {
  useSidepanelCustomHeight,
  useSidepanelRenderedWidth,
  useSidepanelSide,
  useSidepanelVerticalOffset,
} from '../atoms'
import { resolveSidepanelVerticalLayout, type SidepanelVerticalLayout } from '../sidepanel-vertical-layout'

interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}

function toViewportRect(bounds: DockBounds, viewport: ViewportSnapshot): Rect {
  const left = bounds.x - viewport.screenX
  const top = bounds.y - viewport.screenY

  return {
    left,
    top,
    right: left + bounds.width,
    bottom: top + bounds.height,
  }
}

function getBottomInsetRect(inset: number, viewport: ViewportSnapshot): Rect | null {
  if (inset <= 0) {
    return null
  }

  return {
    left: 0,
    top: Math.max(0, viewport.height - inset),
    right: viewport.width,
    bottom: viewport.height,
  }
}

/**
 * The panel's height and vertical position.
 *
 * The dock and the macOS menu bar (which the overlay window covers) are
 * excluded here once, by shortening the *band* the panel lives in, rather than
 * by every descendant subtracting them again — a shortened panel that already
 * sits above the dock must not pay the subtraction a second time.
 *
 * The dock test uses the panel's horizontal extent only. That is known before
 * the height is resolved, so there is no circular dependency: a bottom dock
 * that the panel's column doesn't overlap costs nothing.
 */
export function useSidepanelVerticalLayout(): SidepanelVerticalLayout {
  const dockState = useDockState()
  const sidepanelWidth = useSidepanelRenderedWidth()
  const viewport = useViewportSnapshot()
  const displayDockInsets = useOverlayDisplayDockInsets()
  const side = useSidepanelSide()
  const customHeight = useSidepanelCustomHeight()
  const customOffset = useSidepanelVerticalOffset()
  const menuBarHeight = useOverlayMenuBarHeight()

  const bandBottom = useMemo(() => {
    if (!dockState.isVisible || dockState.position !== 'bottom') {
      return viewport.height
    }

    const panelLeft = side === 'right' ? Math.max(0, viewport.width - sidepanelWidth) : 0
    const panelRight = side === 'right' ? viewport.width : Math.min(viewport.width, sidepanelWidth)

    const dockRect = dockState.liveBounds
      ? toViewportRect(dockState.liveBounds, viewport)
      : dockState.visibleBounds
        ? toViewportRect(dockState.visibleBounds, viewport)
        : getBottomInsetRect(displayDockInsets?.bottom ?? 0, viewport)

    if (!dockRect || panelLeft >= dockRect.right || panelRight <= dockRect.left) {
      return viewport.height
    }

    const covered = Math.min(viewport.height, dockRect.bottom) - Math.max(0, dockRect.top)
    return Math.max(0, viewport.height - Math.max(0, Math.ceil(covered)))
  }, [
    displayDockInsets?.bottom,
    dockState.isVisible,
    dockState.liveBounds,
    dockState.position,
    dockState.visibleBounds,
    side,
    sidepanelWidth,
    viewport,
  ])

  return useMemo(
    () =>
      resolveSidepanelVerticalLayout({
        bandTop: menuBarHeight,
        bandHeight: Math.max(0, bandBottom - menuBarHeight),
        customHeight,
        customOffset,
      }),
    [bandBottom, menuBarHeight, customHeight, customOffset],
  )
}
